import { useCallback, useEffect, useRef, useState } from 'react'
import {
  centsOf,
  dollarsText,
  endDay,
  getOpenCashOuts,
  getOpenDay,
  payCashOut,
  previewEnd,
  startDay,
  type CashOutRequest,
  type DayResult,
  type DrawerPreview,
} from '../lib/day-api'
import { TILL_TIME_ZONE } from '../lib/orders-list'
import { tr, useLang, type Lang } from '../lib/i18n'
import A from '../locales/app.json'
import D from '../locales/day.json'
import C from '../locales/collect.json'
import F from '../locales/final-check.json'

// R572 (b2), the till's Day panel (Rook #69053 rulings 4 and 5, #69117): Start Day, the cash outs
// to pay, and End Day. Before End the panel shows only what Nour entered (her start and the cash
// outs she paid here), never expected or the difference. End is a full screen: a typed count
// first (nothing prefilled), then the server's preview (counted, expected, difference), then the
// dollars handed to Bilal and the confirm. Start, Paid and End refuse offline with a plain line.

type DayState =
  | { kind: 'loading' }
  | { kind: 'none' }
  | { kind: 'open'; day: DrawerPreview }
  | { kind: 'failed' }
  | { kind: 'signedOut' }

type RowState = { kind: 'idle' } | { kind: 'pending' } | { kind: 'refused'; message: string }

type EndState = {
  step: 'count' | 'preview' | 'empty'
  counted: string
  preview: DrawerPreview | null
  handed: string
  note: string
  sending: boolean
  error: string | null
}

const FIELD =
  'w-full min-h-tap rounded-xl border border-fleet-300 bg-white px-4 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy disabled:bg-fleet-100'
const PRIMARY =
  'min-h-tap rounded-xl bg-navy hover:bg-navy/90 disabled:bg-fleet-300 text-white font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2'
const SECONDARY =
  'min-h-tap rounded-xl border-2 border-fleet-300 bg-white hover:bg-fleet-100 text-fleet-700 font-semibold px-4 py-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy focus-visible:ring-offset-2'
const ALERT = 'rounded-lg bg-ember-100 text-ember-700 text-sm font-semibold px-4 py-3'
const LABEL = 'block text-sm font-semibold text-fleet-600 mb-1.5'

function clock(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', { timeZone: TILL_TIME_ZONE, hour: '2-digit', minute: '2-digit', hour12: false })
}

function dollars(totalCents: number): string {
  return `${Math.floor(totalCents / 100)}.${String(totalCents % 100).padStart(2, '0')}`
}

/** The plain line for a failed write: the server's own words, or offline, or unreachable. */
function failureLine(r: Exclude<DayResult<unknown>, { ok: true }>, lang: Lang): string {
  if (r.kind === 'offline') return tr(D.needConnection, lang)
  if (r.kind === 'network') return tr(D.network, lang)
  return r.message
}

export function DayPanel({ isOnline }: { isOnline: boolean }) {
  const lang = useLang()
  const [day, setDay] = useState<DayState>({ kind: 'loading' })
  const [endedAt, setEndedAt] = useState<string | null>(null)
  const [startTyped, setStartTyped] = useState('')
  const [startNote, setStartNote] = useState('')
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const [cashOuts, setCashOuts] = useState<CashOutRequest[] | null>(null)
  const [cashOutsFailed, setCashOutsFailed] = useState(false)
  const [rows, setRows] = useState<Record<string, RowState>>({})
  const [end, setEnd] = useState<EndState | null>(null)
  const [opening, setOpening] = useState(false)
  const [openError, setOpenError] = useState<string | null>(null)
  const pushed = useRef(false)

  const loadDay = useCallback(async () => {
    setDay({ kind: 'loading' })
    const r = await getOpenDay()
    if (r.ok) return setDay({ kind: 'open', day: r.value })
    if (r.kind === 'refused' && r.status === 404) return setDay({ kind: 'none' })
    if (r.kind === 'refused' && r.status === 401) return setDay({ kind: 'signedOut' })
    setDay({ kind: 'failed' })
  }, [])

  const loadCashOuts = useCallback(async () => {
    setCashOutsFailed(false)
    const r = await getOpenCashOuts()
    if (r.ok) return setCashOuts(r.value)
    setCashOutsFailed(true)
  }, [])

  useEffect(() => {
    void loadDay()
    void loadCashOuts()
  }, [loadDay, loadCashOuts])

  // Back closes End: the day stays open and nothing is written.
  const closeEnd = useCallback(() => {
    setEnd(null)
    if (pushed.current) {
      pushed.current = false
      window.history.back()
    }
  }, [])

  useEffect(() => {
    if (end === null) return
    const onPop = () => {
      pushed.current = false
      setEnd(null)
    }
    window.addEventListener('popstate', onPop)
    return () => window.removeEventListener('popstate', onPop)
  }, [end])

  const open = day.kind === 'open' ? day.day : null
  const startAmount = dollarsText(startTyped)
  let startReason: string | null = null
  if (!starting) {
    if (open) startReason = tr(D.startOpen, lang)
    else if (!isOnline) startReason = tr(D.needConnection, lang)
    else if (startAmount === null) startReason = tr(D.needDollars, lang)
  }
  const canStart = !starting && startReason === null && day.kind === 'none'

  async function handleStart() {
    if (!canStart || startAmount === null) return
    setStarting(true)
    setStartError(null)
    const r = await startDay(startAmount, startNote)
    setStarting(false)
    if (!r.ok) return setStartError(failureLine(r, lang))
    setStartTyped('')
    setStartNote('')
    setEndedAt(null)
    setDay({ kind: 'open', day: { ...r.value, empty: true } })
  }

  async function handlePaid(request: CashOutRequest) {
    if ((rows[request.id] ?? { kind: 'idle' }).kind === 'pending') return
    if (!isOnline) {
      setRows((prev) => ({ ...prev, [request.id]: { kind: 'refused', message: tr(D.needConnection, lang) } }))
      return
    }
    setRows((prev) => ({ ...prev, [request.id]: { kind: 'pending' } }))
    const r = await payCashOut(request.id)
    if (!r.ok) {
      setRows((prev) => ({ ...prev, [request.id]: { kind: 'refused', message: failureLine(r, lang) } }))
      return
    }
    setRows((prev) => ({ ...prev, [request.id]: { kind: 'idle' } }))
    setCashOuts((prev) => prev && prev.filter((c) => c.id !== request.id))
    // The server's open day now lists it among the day's paid cash outs.
    const day2 = await getOpenDay()
    if (day2.ok) setDay({ kind: 'open', day: day2.value })
  }

  async function handleOpenEnd() {
    if (opening) return
    setOpenError(null)
    if (!isOnline) return setOpenError(tr(D.needConnection, lang))
    setOpening(true)
    // A fresh read: whether the day is empty (no sales, no cash outs) decides the form.
    const r = await getOpenDay()
    setOpening(false)
    if (!r.ok) return setOpenError(failureLine(r, lang))
    setDay({ kind: 'open', day: r.value })
    window.history.pushState({ tillEndDay: true }, '')
    pushed.current = true
    setEnd({ step: r.value.empty ? 'empty' : 'count', counted: '', preview: null, handed: '', note: '', sending: false, error: null })
  }

  async function handleShowDifference() {
    if (!end || end.sending) return
    const counted = dollarsText(end.counted)
    if (counted === null) return
    if (!isOnline) return setEnd({ ...end, error: tr(D.needConnection, lang) })
    setEnd({ ...end, sending: true, error: null })
    const r = await previewEnd(counted)
    if (!r.ok) return setEnd((prev) => prev && { ...prev, sending: false, error: failureLine(r, lang) })
    setEnd((prev) => prev && { ...prev, sending: false, step: 'preview', preview: r.value, counted })
  }

  async function handleConfirmEnd() {
    if (!end || end.sending) return
    if (!isOnline) return setEnd({ ...end, error: tr(D.needConnection, lang) })
    let body: { countedUsd?: string; handedUsd?: string; endLiraNote?: string }
    if (end.step === 'empty') body = { endLiraNote: end.note }
    else {
      const handed = dollarsText(end.handed)
      const counted = end.preview?.countedUsd ?? end.counted
      if (handed === null || end.step !== 'preview') return
      // Rook #69156 2: the till refuses handed over counted before the confirm ((a2) is the wall).
      if (centsOf(handed) > centsOf(counted)) return setEnd({ ...end, error: tr(D.handedOverCounted, lang) })
      body = { countedUsd: counted, handedUsd: handed, endLiraNote: end.note }
    }
    setEnd({ ...end, sending: true, error: null })
    const r = await endDay(body)
    if (!r.ok) return setEnd((prev) => prev && { ...prev, sending: false, error: failureLine(r, lang) })
    setEndedAt(r.value.endedAt)
    setDay({ kind: 'none' })
    closeEnd()
  }

  // Rook #69156 1: the day's paid cash outs come from the server's open day, so a reload loses none.
  const paid = open?.paidCashOuts ?? []
  const paidCents = paid.reduce((sum, c) => sum + centsOf(c.amountUsd), 0)

  return (
    <section id="day-face" aria-labelledby="day-title" className="min-h-screen bg-fleet-50 px-6 py-8 font-body text-fleet-800">
      <div className="w-full max-w-sm mx-auto space-y-6">
        <div className="flex items-center justify-between gap-3">
          <h1 id="day-title" className="sr-only">
            {tr(A.tabDay, lang)}
          </h1>
          <span aria-hidden="true" />
          <button
            id="day-refresh-btn"
            type="button"
            onClick={() => {
              void loadDay()
              void loadCashOuts()
            }}
            className="min-h-tap rounded-lg px-2 text-base font-semibold text-navy hover:bg-navy-tint focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy"
          >
            {tr(C.refresh, lang)}
          </button>
        </div>

        {endedAt && (
          <p id="day-ended" role="status" className="rounded-lg bg-jade-100 text-jade-700 text-sm font-semibold px-4 py-3">
            {tr(D.endDone, lang, { time: clock(endedAt) })}
          </p>
        )}

        {day.kind === 'loading' && <p className="text-sm text-fleet-500">{tr(C.loading, lang)}</p>}
        {day.kind === 'signedOut' && (
          <p id="day-signed-out" role="alert" className={ALERT}>
            {tr(D.signedOut, lang)}
          </p>
        )}
        {day.kind === 'failed' && (
          <div className="space-y-3">
            <p id="day-load-error" role="alert" className={ALERT}>
              {tr(D.loadFailed, lang)}
            </p>
            <button id="day-retry-btn" type="button" onClick={() => void loadDay()} className={`w-full ${SECONDARY}`}>
              {tr(F.tryAgain, lang)}
            </button>
          </div>
        )}

        {day.kind === 'none' && (
          <p id="day-none" className="text-sm text-fleet-500">
            {tr(D.noDay, lang)}
          </p>
        )}

        {open && (
          <section id="day-today" aria-labelledby="day-today-title" className="rounded-2xl border border-fleet-200 bg-white p-4 space-y-2">
            <h2 id="day-today-title" className="text-lg font-semibold text-fleet-900">
              {tr(D.todayTitle, lang)}
            </h2>
            <p id="day-started">{tr(D.startedLine, lang, { time: clock(open.startedAt), amount: open.startUsd })}</p>
            {open.startLiraNote && <p className="text-sm text-fleet-600">{tr(D.startedNote, lang, { note: open.startLiraNote })}</p>}
            <p id="day-paid-total">{tr(D.paidTotal, lang, { amount: dollars(paidCents) })}</p>
            {paid.length > 0 && (
              <ul id="day-paid-list" className="text-sm text-fleet-600 space-y-0.5">
                {paid.map((c) => (
                  <li key={c.id}>{tr(D.paidDone, lang, { payee: c.payeeText, amount: c.amountUsd })}</li>
                ))}
              </ul>
            )}
          </section>
        )}

        {(day.kind === 'none' || open) && (
          <section id="day-start" aria-labelledby="day-start-title" className="rounded-2xl border border-fleet-200 bg-white p-4 space-y-3">
            <h2 id="day-start-title" className="text-lg font-semibold text-fleet-900">
              {tr(D.startTitle, lang)}
            </h2>
            <div>
              <label htmlFor="day-start-amount" className={LABEL}>
                {tr(D.startAmount, lang)}
              </label>
              <input
                id="day-start-amount"
                type="text"
                inputMode="decimal"
                dir="ltr"
                value={startTyped}
                disabled={open !== null || starting}
                onChange={(e) => setStartTyped(e.target.value)}
                className={`${FIELD} text-end`}
              />
            </div>
            <div>
              <label htmlFor="day-start-note" className={LABEL}>
                {tr(D.startNote, lang)}
              </label>
              <input
                id="day-start-note"
                type="text"
                maxLength={200}
                value={startNote}
                disabled={open !== null || starting}
                onChange={(e) => setStartNote(e.target.value)}
                className={FIELD}
              />
            </div>
            {startError && (
              <p id="day-start-error" role="alert" className={ALERT}>
                {startError}
              </p>
            )}
            <button id="day-start-btn" type="button" disabled={!canStart} aria-busy={starting} onClick={() => void handleStart()} className={`w-full ${PRIMARY}`}>
              {starting ? tr(D.starting, lang) : tr(D.startButton, lang)}
            </button>
            {startReason && (
              <p id="day-start-reason" className="text-sm text-fleet-500">
                {startReason}
              </p>
            )}
          </section>
        )}

        <section id="day-cash-outs" aria-labelledby="day-cash-outs-title" className="space-y-3">
          <h2 id="day-cash-outs-title" className="text-lg font-semibold text-fleet-900">
            {tr(D.cashOutsTitle, lang)}
          </h2>
          {cashOutsFailed && (
            <div className="space-y-3">
              <p id="cash-outs-load-error" role="alert" className={ALERT}>
                {tr(D.cashOutsLoadFailed, lang)}
              </p>
              <button id="cash-outs-retry-btn" type="button" onClick={() => void loadCashOuts()} className={`w-full ${SECONDARY}`}>
                {tr(F.tryAgain, lang)}
              </button>
            </div>
          )}
          {cashOuts !== null && cashOuts.length === 0 && !cashOutsFailed && (
            <p id="cash-outs-empty" className="text-sm text-fleet-500">
              {tr(D.cashOutsEmpty, lang)}
            </p>
          )}
          {cashOuts !== null && cashOuts.length > 0 && (
            <ul id="cash-outs-list" className="space-y-3">
              {cashOuts.map((c) => {
                const state = rows[c.id] ?? { kind: 'idle' }
                const pending = state.kind === 'pending'
                return (
                  <li key={c.id} data-cash-out-id={c.id} className="rounded-xl border border-fleet-300 bg-white px-4 py-3 space-y-2">
                    <div className="flex items-baseline justify-between gap-3">
                      <span className="font-semibold">
                        {c.payeeText} <span className="text-sm text-fleet-500">({tr(c.payeeKind === 'staff' ? D.kindStaff : D.kindExternal, lang)})</span>
                      </span>
                      <span dir="ltr" className="font-bold text-navy tabular-nums">
                        ${c.amountUsd}
                      </span>
                    </div>
                    <p className="text-sm text-fleet-600">{c.reason}</p>
                    {c.raisedByName && <p className="text-xs text-fleet-500">{tr(D.raisedBy, lang, { name: c.raisedByName })}</p>}
                    {state.kind === 'refused' && (
                      <p role="alert" className={ALERT}>
                        {state.message}
                      </p>
                    )}
                    <button type="button" data-paid-btn disabled={pending} aria-busy={pending} onClick={() => void handlePaid(c)} className={`w-full ${PRIMARY}`}>
                      {pending ? tr(D.paying, lang) : tr(D.paid, lang)}
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </section>

        {open && (
          <div className="space-y-2">
            {openError && (
              <p id="day-end-open-error" role="alert" className={ALERT}>
                {openError}
              </p>
            )}
            <button id="day-end-btn" type="button" disabled={opening} aria-busy={opening} onClick={() => void handleOpenEnd()} className={`w-full ${SECONDARY}`}>
              {tr(D.endButton, lang)}
            </button>
          </div>
        )}
      </div>

      {end && (
        <EndScreen
          end={end}
          lang={lang}
          isOnline={isOnline}
          onChange={(next) => setEnd(next)}
          onShow={() => void handleShowDifference()}
          onConfirm={() => void handleConfirmEnd()}
          onCancel={closeEnd}
        />
      )}
    </section>
  )
}

function EndScreen({
  end,
  lang,
  isOnline,
  onChange,
  onShow,
  onConfirm,
  onCancel,
}: {
  end: EndState
  lang: Lang
  isOnline: boolean
  onChange: (next: EndState) => void
  onShow: () => void
  onConfirm: () => void
  onCancel: () => void
}) {
  const counted = dollarsText(end.counted)
  const handed = dollarsText(end.handed)
  let reason: string | null = null
  if (!end.sending) {
    if (!isOnline) reason = tr(D.needConnection, lang)
    else if (end.step === 'count' && counted === null) reason = tr(D.needDollars, lang)
    else if (end.step === 'preview' && handed === null) reason = tr(D.needDollars, lang)
    else if (end.step === 'preview' && handed !== null && centsOf(handed) > centsOf(end.preview?.countedUsd ?? end.counted))
      reason = tr(D.handedOverCounted, lang)
  }
  const ready = !end.sending && reason === null

  return (
    <div
      id="end-day-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="end-day-title"
      className="fixed inset-0 z-[60] overflow-y-auto bg-fleet-50 px-6 py-8 font-body text-fleet-800"
    >
      <div className="w-full max-w-sm mx-auto space-y-4">
        <h2 id="end-day-title" className="font-condensed text-2xl font-bold uppercase tracking-wide">
          {tr(D.endTitle, lang)}
        </h2>

        {end.step === 'empty' && (
          <p id="end-day-empty" className="text-sm text-fleet-600">
            {tr(D.endEmpty, lang)}
          </p>
        )}

        {end.step === 'count' && (
          <div>
            <label htmlFor="end-day-counted" className={LABEL}>
              {tr(D.endCountLabel, lang)}
            </label>
            <input
              id="end-day-counted"
              type="text"
              inputMode="decimal"
              dir="ltr"
              autoComplete="off"
              value={end.counted}
              disabled={end.sending}
              onChange={(e) => onChange({ ...end, counted: e.target.value, error: null })}
              className={`${FIELD} text-end`}
            />
          </div>
        )}

        {end.step === 'preview' && end.preview && (
          <dl id="end-day-preview" className="rounded-2xl border border-fleet-200 bg-white p-4 grid grid-cols-2 gap-y-2">
            <dt>{tr(D.endCounted, lang)}</dt>
            <dd id="end-day-counted-shown" dir="ltr" className="text-end font-semibold tabular-nums">
              ${end.preview.countedUsd ?? end.counted}
            </dd>
            <dt>{tr(D.endExpected, lang)}</dt>
            <dd id="end-day-expected" dir="ltr" className="text-end font-semibold tabular-nums">
              ${end.preview.expectedUsd ?? ''}
            </dd>
            <dt>{tr(D.endDifference, lang)}</dt>
            <dd id="end-day-difference" dir="ltr" className="text-end font-bold tabular-nums">
              ${end.preview.differenceUsd ?? ''}
            </dd>
          </dl>
        )}

        {end.step === 'preview' && (
          <div>
            <label htmlFor="end-day-handed" className={LABEL}>
              {tr(D.endHanded, lang)}
            </label>
            <input
              id="end-day-handed"
              type="text"
              inputMode="decimal"
              dir="ltr"
              autoComplete="off"
              value={end.handed}
              disabled={end.sending}
              onChange={(e) => onChange({ ...end, handed: e.target.value, error: null })}
              className={`${FIELD} text-end`}
            />
          </div>
        )}

        {end.step !== 'count' && (
          <div>
            <label htmlFor="end-day-note" className={LABEL}>
              {tr(D.endNote, lang)}
            </label>
            <input
              id="end-day-note"
              type="text"
              maxLength={200}
              value={end.note}
              disabled={end.sending}
              onChange={(e) => onChange({ ...end, note: e.target.value })}
              className={FIELD}
            />
          </div>
        )}

        {end.error && (
          <p id="end-day-error" role="alert" className={ALERT}>
            {end.error}
          </p>
        )}

        <div className="flex flex-col gap-3">
          {end.step === 'count' ? (
            <button id="end-day-show-btn" type="button" disabled={!ready} aria-busy={end.sending} onClick={onShow} className={PRIMARY}>
              {end.sending ? tr(D.endChecking, lang) : tr(D.endShow, lang)}
            </button>
          ) : (
            <button id="end-day-confirm-btn" type="button" disabled={!ready} aria-busy={end.sending} onClick={onConfirm} className={PRIMARY}>
              {end.sending ? tr(D.ending, lang) : tr(D.endConfirm, lang)}
            </button>
          )}
          {reason && (
            <p id="end-day-reason" className="text-sm text-fleet-500">
              {reason}
            </p>
          )}
          <button id="end-day-cancel-btn" type="button" disabled={end.sending} onClick={onCancel} className={SECONDARY}>
            {tr(D.endCancel, lang)}
          </button>
        </div>
      </div>
    </div>
  )
}
